import {watch} from 'vue';
import type {I18n} from 'vue-i18n';
import type {RouteLocationNormalized, Router} from 'vue-router';
import ConfigProvider from '@core/services/ConfigProvider';

/**
 * Maps route names to vue-i18n title keys, so every route resolves a page title
 * for `document.title` (WCAG 2.4.2) without duplicating `meta.title` on each
 * route. Existing nav labels are reused; detail routes share their section
 * label. An explicit `meta.title` always takes precedence over this map.
 */
const ROUTE_TITLE_KEYS: Record<string, string> = {
  // core
  releasenotes: 'core.releases',
  issues: 'core.submitRequest',
  settings_general: 'core.generalSettingsPageTitle',
  settings_theme: 'core.themeSettings',
  settings_uploads: 'core.uploads',
  settings_emailTemplates_details: 'core.emailTemplates',
  // internal devportal
  dashboard: 'internalDevPortal.homeMsg',
  admin: 'internalDevPortal.admin',
  admin_pendingapprovals: 'internalDevPortal.pendingApprovals',
  admin_pendingapprovals_detail: 'internalDevPortal.pendingApprovals',
  admin_logging: 'internalDevPortal.logging',
  admin_permissionGroups: 'internalDevPortal.permissionGroups',
  admin_permissionGroups_details: 'internalDevPortal.permissionGroups',
  admin_permissionGroups_details_teams: 'internalDevPortal.permissionGroups',
  admin_environments: 'internalDevPortal.environments',
  admin_environments_create: 'internalDevPortal.environments',
  admin_environments_details: 'internalDevPortal.environments',
  apps: 'internalDevPortal.myApplicationMsg',
  app_details: 'internalDevPortal.myApplicationMsg',
  search_documentation: 'internalDevPortal.findDocumentationMsg',
  products: 'internalDevPortal.apiMsg',
  product_details: 'internalDevPortal.apiMsg',
  analytics: 'internalDevPortal.analytics',
  team_list: 'internalDevPortal.myTeamMsg',
  team_details: 'internalDevPortal.myTeamMsg',
  team_details_permissions: 'internalDevPortal.myTeamMsg',
  team_details_apis: 'internalDevPortal.myTeamMsg',
  team_details_applications: 'internalDevPortal.myTeamMsg',
  team_details_settings: 'internalDevPortal.myTeamMsg',
  pages: 'internalDevPortal.documentation',
  // subscription manager
  Companies: 'internalDevPortal.companyMsg',
  CompanyDetails: 'internalDevPortal.companyMsg',
  // portfolio
  portfolioDashboard: 'portfolio.dashboard',
  prospect: 'portfolio.prospects',
  prospectDetail: 'portfolio.prospects',
  solution: 'portfolio.solutions',
  solutionDetail: 'portfolio.solutions',
  solutionProspectDetail: 'portfolio.prospects',
  exceptionlist: 'portfolio.exceptions',
  AdaAgenda: 'portfolio.adaAgenda',
  AdminUsageType: 'portfolio.usageTypes',
  AdminNorthBound: 'portfolio.newNorthBoundMsg',
  AdminSouthBound: 'portfolio.newSouthBoundMsg',
  AdminAbstractionType: 'portfolio.abstractionType',
  AdminPortfolioRelease: 'portfolio.portfolioReleases',
  // domain
  AdminDomain: 'portfolio.domains',
  AdminDomainDetail: 'portfolio.domains',
  AdminDomainDataDictionary: 'domain.domains',
  AdminDomainDetailDataDictionary: 'domain.domains',
  // data dictionary
  DataDictionaryDashboard: 'dataDictionary.dashboard',
  ApiSchemaImport: 'dataDictionary.importSpecification',
  ImportLogs: 'dataDictionary.importLogs',
  BusinessObjects: 'dataDictionary.objects',
  BusinessObjectDetails: 'dataDictionary.objects',
  Terms: 'dataDictionary.terms',
  term_details: 'dataDictionary.terms',
  SpecificationGenerator: 'dataDictionary.menuSpecificationGenerate',
};

function pickRouteTitleKey(route: RouteLocationNormalized): string | undefined {
  for (let i = route.matched.length - 1; i >= 0; i--) {
    const title = route.matched[i]?.meta?.title;
    if (typeof title === 'string' && title.trim() !== '') {
      return title;
    }
  }
  for (let i = route.matched.length - 1; i >= 0; i--) {
    const name = route.matched[i]?.name;
    if (typeof name === 'string' && ROUTE_TITLE_KEYS[name]) {
      return ROUTE_TITLE_KEYS[name];
    }
  }
  return undefined;
}

/**
 * Brand suffix for `document.title`. External clients expose `{client}.metaTitle`;
 * everything else (incl. the apidex shell) falls back to `core.metaTitle`.
 */
export function resolveTitleSuffix(
  t: (key: string) => string,
  te: (key: string) => boolean,
): string {
  const client = ConfigProvider.value('client') as string | undefined;
  if (client && client !== 'default') {
    const key = `${client}.metaTitle`;
    if (te(key)) {
      return t(key);
    }
  }
  return t('core.metaTitle');
}

/**
 * Composes `{page} – {brand}` (or just `{brand}` when no page title is known).
 * Used by external Strapi shells that derive their title from fetched content.
 */
export function composeDocumentTitle(
  pageTitle: string | null | undefined,
  t: (key: string) => string,
  te: (key: string) => boolean,
): string {
  const suffix = resolveTitleSuffix(t, te);
  const trimmed = typeof pageTitle === 'string' ? pageTitle.trim() : '';
  return trimmed ? `${trimmed} – ${suffix}` : suffix;
}

/**
 * Resolves `document.title` from the current route and i18n.
 * Returns `null` when an external client shell should keep its own title.
 */
export function resolveDocumentTitle(
  route: RouteLocationNormalized,
  t: (key: string) => string,
): string | null {
  const key = pickRouteTitleKey(route);
  if (key) {
    return `${t(key)} – ${t('core.metaTitle')}`;
  }
  const client = ConfigProvider.value('client') as string | undefined;
  if (!client || client === 'default') {
    return t('core.metaTitle');
  }
  return null;
}

/** Keep `document.title` aligned with route meta and locale (WCAG 2.4.2). */
export function registerDocumentRouteSync(router: Router, i18n: I18n) {
  function apply() {
    const next = resolveDocumentTitle(router.currentRoute.value, i18n.global.t);
    if (next) {
      document.title = next;
    }
  }

  router.afterEach(() => {
    apply();
  });

  watch(
    () => i18n.global.locale.value,
    () => {
      apply();
    },
  );
}
